import type { BubbleConfig } from './types';

/** Bubble ellipses on the light side of the wave. Coords in viewBox(0 0 1920 300) */
export const WAVE_BUBBLES: BubbleConfig[] = [
  // Right side, large
  { cx: 1410.75, cy: 190, rx: 42.5, ry: 22, rotate: -7.25, delay: 4 },
  // Right center, small
  { cx: 1327, cy: 186.5, rx: 7.25, ry: 3.75, delay: 6 },

  // Center cluster
  { cx: 970.75, cy: 129.5, rx: 7.25, ry: 4.75, delay: 2 },
  { cx: 999, cy: 118.5, rx: 12.75, ry: 8.75, delay: 0 },
  { cx: 861.75, cy: 109, rx: 7.25, ry: 3.75, delay: 3 },

  // Center left, large
  { cx: 813, cy: 124.5, rx: 32, ry: 14.75, rotate: 8.5, delay: 1 },

  // Left side
  { cx: 402.25, cy: 170.5, rx: 14.75, ry: 7.5, delay: 5 },
  { cx: 368.75, cy: 150.5, rx: 7.25, ry: 3.75, delay: 7 },
  { cx: 341, cy: 163, rx: 20.5, ry: 12.25, delay: 3 },
];

/** Bubble animation timing in frames (30fps) */
export const BUBBLE_ANIMATION = {
  cycleDuration: 83, // ~2.75s per pulse
  staggerStep: 8, // ~0.25s between bubbles
  peakStart: 0.15, // fraction when fully visible
  peakEnd: 0.7, // fraction when fade-out starts
};

/** Wave fill colors */
export const WAVE_COLORS = {
  waveFill: '#ffffff',
  bubbleFill: '#BDDFFA',
};

/** Wave slide-in and ripple config (frames at 30fps) */
export const WAVE_ANIMATION = {
  startTop: -35, // initial vertical offset (%)
  endTop: 0, // final vertical offset (%)
  duration: 90, // slide-in duration
  ripplePeriod: 150, // ~5s per cycle
  rippleAmplitude: 25, // SVG units of vertical shift
};

/** Photo flash animation config (frames at 30fps) */
export const PHOTO_FLASH_ANIMATION = {
  // Starts after wave slide-in (WAVE_ANIMATION.duration = 90)
  cameraDropStart: 90,
  cameraDropEnd: 120, // 30-frame drop
  cameraDropHeight: 200, // px above final position at start of drop

  // Center of camera element (top-left: 1350,535 — size: 100x70)
  cameraLeft: '1400px',
  cameraTop: '570px',

  flashStart: 120,
  flashEnd: 140,

  // Center of flower element (top-left: 1290,540 — size: 55x56)
  flowerJumpStart: 135,
  flowerJumpEnd: 165,
  jumpDistance: 84, // px leftward (1400 - 1316)
  jumpYOffset: 41, // px downward (570 - 529)
  jumpArcHeight: 80, // px arc peak

  flowerSpinStart: 165,
  spinPeriod: 90, // frames per full rotation
};

/** Camera and flower rendered sizes matching design SVG clip dimensions (1920x1080 canvas) */
export const CAMERA_SIZE = 100;
export const FLOWER_SIZE = 55;

/** Flash effect color */
export const FLASH_COLOR = 'rgba(255, 255, 255, 0.9)';

/** 8 alternating thick/thin rays forming a starburst around the camera */
export const FLASH_RAYS = [
  { angle: 0, length: 70, width: 5 },
  { angle: 45, length: 50, width: 3 },
  { angle: 90, length: 75, width: 5 },
  { angle: 135, length: 45, width: 3 },
  { angle: 180, length: 68, width: 5 },
  { angle: 225, length: 42, width: 3 },
  { angle: 270, length: 72, width: 5 },
  { angle: 315, length: 48, width: 3 },
];

/** Typing text animation config */
export const TYPING_TEXT_ANIMATION = {
  // Starts when camera drop completes
  startFrame: 120,
  left: 280,
  top: 400,
  rotation: -6, // degrees of tilt
  maxWidth: 1350,
  charsPerFrame: 2,

  // Normal text
  normal: {
    fontSize: 120,
    lineHeight: 110,
  },

  // Highlight text appears inline after normal text
  highlight: {
    fontSize: 160,
    lineHeight: 90,
    marginLeft: 8,
    charsPerFrame: 1.5,
  },

  // Text content
  normalText: 'Training packed its bags and went on \nBack soon!',
  highlightText: 'holiday.',
};

/** Wave band config: paths, color, and reveal timing */
export const WAVE_BAND = {
  color: '#ffffff',
  drawDuration: 60, // frames per reveal cycle
  paths: {
    front:
      'M400.26 874.537C183.15 840.008 -50.0636 898.79 -139.531 932.497L-155.934 983.06C154.223 812.873 382.367 879.468 492.711 894.267C603.055 909.066 784.974 978.127 981.804 872.069C1178.63 766.01 1263.44 780.353 1424.67 804.241C1585.9 828.128 1679.66 788.208 1751.23 746.278C1808.49 712.734 1924.2 699.415 1974.9 696.949C1925.69 695.716 1812.37 700.65 1752.72 730.248C1678.17 767.245 1520.11 805.474 1357.57 784.509C1195.04 763.544 1083.2 817.808 975.84 859.738C868.478 901.668 671.648 917.7 400.26 874.537Z',
    back: 'M1602.03 845.861C1914.51 752.293 2267.79 746.024 2405.37 754.585L2438.75 799.782C1952 717.676 1629.22 845.794 1469.9 890.645C1310.58 935.497 1056.12 1053.34 747.582 1003.15C443.015 953.602 320.306 989.397 93.6882 1055.5L84.7615 1058.11C-147.611 1125.87 -292.63 1112.38 -405.446 1090.82C-495.698 1073.57 -668.07 1092.26 -742.974 1103.76C-670.94 1089.03 -503.613 1062.76 -410.583 1075.47C-294.294 1091.36 -55.1549 1085.53 179.672 1020.28C414.499 955.036 588.705 977.661 754.074 989.391C919.443 1001.12 1211.44 962.821 1602.03 845.861Z',
  },
};

/** Decorative tree: color, SVG paths, and sway animation config */
export const TREE_ANIMATION = {
  color: '#0C408F',
  fadeInDuration: 30, // frames until fully visible
  swayPeriodFrames: 90, // ~3s per cycle
  swayMaxDeg: 2, // peak rotation in degrees
  paths: [
    'M434.716 314.572q-2.694-66.344-12.741-100.92c-5.474 41.182-10.31 78.789-14.031 112.079-3.859 33.831-.811 65.431 9.005 95.34 13.327-27.044 19.023-62.41 17.567-106.839M241.503 531.958c23.08-5.788 54.263-19.54 92.871-40.858s66.492-44.567 82.575-70.029c-30.587 6.998-59.473 20.773-86.321 41.126-27.187 20.552-56.828 43.919-89.125 69.761m224.966-300.087c-5.892 42.802-10.668 81.287-14.468 115.997-3.599 35.05-.63 68.069 9.246 98.858 13.806-27.783 19.781-64.229 18.204-110.417-1.916-45.988-6.423-80.847-13.181-104.778M280.232 561.343c23.758-6.187 56.159-20.198 96.125-42.313s68.328-46.104 85.229-72.504c-31.804 7.256-61.709 21.63-89.575 42.581q-41.798 31.426-91.979 71.896zm97.792-162.222c12.709-25.766 18.125-60.054 16.65-102.184-1.874-42.811-5.661-74.891-11.96-97.262-5.595 39.424-10.013 75.412-13.454 107.624s-.453 62.932 8.764 91.822M209.505 505.479c21.862-5.529 51.488-18.824 89.277-39.203 37.251-20.52 63.778-42.972 79.242-67.155-29.369 6.74-57.238 19.916-83.267 39.331-25.829 19.754-54.313 41.983-85.252 67.027m120.288-228.046c-4.859 32.131-2.749 62.91 5.789 92.199 13.447-25.285 20.282-59.492 20.024-101.88q-.685-64.094-15.795-99.126c-2.698 40.467-6.038 76.736-10.357 109.007m5.928 91.659c-29.629 5.52-57.898 18.017-84.326 36.751-26.429 18.734-57.99 37.283-94.003 55.246 29.591-.924 62.832-10.397 99.185-28.559 36.353-18.163 62.601-39.536 79.144-63.438m-76.074 2.626c26.115-5.284 44.802-10.778 55.522-16.62-57.907-5.306-114.771 2.04-171.13 21.898 50.977 2.059 89.693.347 115.608-5.278m55.861-16.82q1.518-24.22-9.135-71.478c-7.101-31.505-24.25-73.115-50.768-125.228 6.34 74.493 26.534 139.928 59.903 196.706m-97.587-17.664c26.115-5.285 44.602-11.118 55.322-16.961-57.907-5.306-114.771 2.041-171.13 21.898 50.977 2.06 89.693.348 115.608-5.277m-1.796-191.06c.32 2.099.441 3.857.561 5.616q13.098 94.536 56.557 168.483c1.351-16.346-1.694-40.172-8.935-71.138-7.101-31.505-24.25-73.115-50.768-125.228.342 7.575 1.224 15.29 2.585 22.267m-52.55 8.47c4.735 7.281 8.271 12.521 10.809 16.061 4.535 6.941 11.909 21.818 21.722 43.954 4.897 12.217 8.995 23.073 12.094 32.23 12.607 19.121 28.05 38.406 45.649 58.252-10.654-26.673-24.542-54.189-42.005-82.349-14.187-24.139-30.749-46.882-48.469-68.488m.2.34a411 411 0 0 1-15.684-22.802c17.254 53.441 36.699 95.074 57.458 124.957 2.537 3.54 4.874 6.74 7.212 9.941-.181-2.638-.761-5.957-1.881-9.415-.92-3.119-1.7-6.777-2.82-10.236-2.759-9.357-6.857-20.213-12.093-32.231-9.474-22.335-16.648-36.872-21.723-43.953-2.198-3.74-6.073-8.781-10.809-16.061-3.076-3.681-6.153-7.362-9.369-10.504 3.077 3.681 6.293 6.823 9.369 10.504m-46.951 74.708q10.275 6.997 43.796 20.917c22.347 9.28 40.001 14.457 52.481 16.271-30.4-23.759-75.615-44.758-135.304-63.196 19.413 12.834 32.376 21.683 39.226 26.348m31.304 36.95a1132 1132 0 0 0-76.28-13.262c29.737 14.085 60.172 25.474 91.843 34.305s61.545 14.603 90.499 17.256c-38.345-18.632-73.632-31.285-106.062-38.299',
    'M212.022 266.6c-30.4-23.759-75.615-44.758-135.304-63.196zm0 0c-2.338-3.201-4.875-6.741-7.212-9.941-20.759-29.883-40.204-71.516-57.458-124.957 6.274 9.121 11.548 16.542 15.684 22.802 17.72 21.606 33.743 44.208 48.469 68.488 17.263 27.82 31.151 55.336 42.005 82.349-28.614-2.852-59.027-8.764-90.499-17.256-31.671-8.831-62.645-20.36-91.843-34.305a1132 1132 0 0 1 76.28 13.262c32.43 7.014 67.917 20.007 106.062 38.299-17.6-19.846-33.042-39.131-45.649-58.252 1.119 3.459 1.899 7.117 2.819 10.236 1.12 3.459 1.5 6.437 1.881 9.415zm0 0c-12.48-1.814-29.794-7.191-52.481-16.271q-33.52-13.92-43.796-20.917c-6.851-4.665-20.153-13.314-39.227-26.348m196.186 117.069c1.351-16.346-1.694-40.172-8.934-71.138-7.102-31.505-24.251-73.115-50.769-125.228.343 7.575 1.224 15.29 2.585 22.267.32 2.099.441 3.857.561 5.616q13.098 94.536 56.557 168.483m0 0c-57.907-5.306-114.77 2.041-171.129 21.898 50.977 2.06 89.692.348 115.607-5.277 26.115-5.285 44.603-11.118 55.323-16.961zm-77.337-105.954c4.897 12.217 8.995 23.074 12.094 32.23zm-21.522-43.614c4.535 6.941 11.909 21.819 21.722 43.954zm-10.809-16.061c4.735 7.281 8.271 12.521 10.809 16.061zm0 0c-3.077-3.681-6.153-7.362-9.369-10.503zm152.133 200.594q1.518-24.22-9.135-71.478c-7.101-31.505-24.25-73.115-50.768-125.228 6.34 74.493 26.534 139.928 59.903 196.706m0 0c-57.907-5.306-114.771 2.041-171.13 21.898 50.977 2.06 89.693.348 115.608-5.277 26.115-5.285 44.802-10.778 55.522-16.621m20.213 14.194c-29.629 5.52-57.898 18.017-84.326 36.751-26.429 18.734-57.99 37.283-94.003 55.246 29.591-.924 62.832-10.397 99.185-28.559 36.353-18.163 62.601-39.536 79.144-63.438m0 0c-8.538-29.289-10.648-60.068-5.789-92.199 4.32-32.27 7.659-68.539 10.358-109.006q14.601 35.331 15.795 99.125c.257 42.389-6.577 76.595-20.025 101.88zm126.205 77.234c-31.805 7.256-61.71 21.63-89.576 42.581q-41.798 31.426-91.979 71.896c23.759-6.187 56.16-20.198 96.125-42.313s68.328-46.104 85.23-72.504zm0 0c-9.877-30.789-13.385-63.948-9.246-98.858 3.799-34.709 8.375-73.535 14.468-115.996 6.759 23.93 11.266 58.79 13.182 104.778 1.376 45.848-4.399 82.633-18.205 110.416zm-83.763-47.745c-29.369 6.74-57.237 19.916-83.267 39.331-25.829 19.754-54.313 41.983-85.252 67.027 21.862-5.529 51.488-18.824 89.277-39.203 37.251-20.52 63.778-42.972 79.242-67.155m0 0c-9.556-28.69-12.544-59.411-8.763-91.822 3.441-32.212 8.198-68.4 13.453-107.624q9.657 32.747 11.961 97.262c1.674 42.47-3.942 76.418-16.651 102.184m39.125 22.29c-30.587 6.998-59.473 20.773-86.321 41.126-27.187 20.552-56.828 43.919-89.124 69.761 23.079-5.788 54.262-19.54 92.87-40.858s66.492-44.567 82.575-70.029m0 0c-9.816-29.909-12.864-61.509-9.004-95.34 3.52-33.63 8.017-71.037 14.03-112.079q10.048 34.576 12.741 100.92c1.795 44.229-4.24 79.795-17.567 106.839z',
    'M111.31 128.641c.882 7.716 9.455 24.633 25.319 50.074 17.882 26.541 37.061 51.406 58.076 74.734 63.645 71.004 149.151 136.478 256.041 197.161 34.891 19.747 67.505 37.173 98.041 52.619 2.933-3.554 4.31-6.65 3.051-9.57-31.355-14.506-64.647-31.534-98.999-51.14-106.889-60.684-192.057-126.357-256.041-197.162-21.015-23.328-40.533-47.993-58.076-74.734-12.068-18.98-21.139-32.861-27.412-41.982',
    'M138.722 170.623c17.882 26.542 37.061 51.406 58.076 74.734zm-27.412-41.982c6.273 9.121 15.344 23.002 27.412 41.982zm0 0c.542 7.915 9.455 24.633 25.319 50.074 17.882 26.541 37.061 51.406 58.076 74.734 63.645 71.004 149.151 136.478 256.041 197.161 34.891 19.747 67.505 37.174 98.241 52.959 2.933-3.554 4.31-6.65 3.051-9.57-31.355-14.506-64.647-31.534-98.999-51.14-106.89-60.684-192.057-126.357-256.041-197.162',
  ],
};
